/* eslint-disable max-lines-per-function */
import { useEffect, useState } from 'react';

import type { ClientOptionModel, ProjectOptionModel } from '@/domain/models/user.js';
import type { UserController } from '@/presentation/controllers/user-controller.js';
import type { AssignmentRow } from '@/presentation/pages/users/utils/validate-user-form.js';

export function useUserForm(controller: UserController, open: boolean, isEdit: boolean, userId: string | undefined) {
    const [loading, setLoading] = useState(false);

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [status, setStatus] = useState<'ATIVO' | 'BLOQUEADO'>('ATIVO');
    const [createdAt, setCreatedAt] = useState('');
    const [lastLoginAt, setLastLoginAt] = useState<string | null>(null);

    const [assignments, setAssignments] = useState<AssignmentRow[]>([{ id: crypto.randomUUID(), clientId: '', projectId: '', profile: 'CF' }]);
    const [clients, setClients] = useState<ClientOptionModel[]>([]);
    const [projectsMap, setProjectsMap] = useState<Record<string, ProjectOptionModel[]>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});

    const loadProjects = (clientId: string) => {
        if (!clientId || projectsMap[clientId]) {
            return;
        }
        controller.loadProjectOptions(clientId).then((result) => {
            result.fold(
                () => {},
                (data) => setProjectsMap((prev) => ({ ...prev, [clientId]: data }))
            );
        });
    };

    useEffect(() => {
        if (!open) {
            return;
        }
        let cancelled = false;
        setName('');
        setEmail('');
        setPhone('');
        setStatus('ATIVO');
        setCreatedAt('');
        setLastLoginAt(null);
        setAssignments([{ id: crypto.randomUUID(), clientId: '', projectId: '', profile: 'CF' }]);
        setErrors({});
        setProjectsMap({});

        controller.loadClientOptions().then((result) => {
            if (cancelled) {
                return;
            }
            result.fold(
                () => {},
                (data) => setClients(data)
            );
        });

        if (isEdit && userId) {
            setLoading(true);
            Promise.all([controller.loadUserById(userId), controller.loadUserAssignments(userId)])
                .then(([userResult, assignmentsResult]) => {
                    if (cancelled) {
                        return;
                    }
                    userResult.fold(
                        () => {},
                        (user) => {
                            setName(user.name);
                            setEmail(user.email);
                            setPhone(user.phone ?? '');
                            setStatus(user.status);
                            setCreatedAt(user.createdAt);
                            setLastLoginAt(user.lastLoginAt);
                        }
                    );
                    assignmentsResult.fold(
                        () => {},
                        (data) => {
                            if (data.length > 0) {
                                const rows = data.map((a) => ({
                                    id: crypto.randomUUID(),
                                    clientId: a.clientId,
                                    projectId: a.projectId ?? '',
                                    profile: a.profile
                                }));
                                setAssignments(rows);
                                const clientIds = [...new Set(rows.map((r) => r.clientId))];
                                clientIds.forEach((cid) => {
                                    controller.loadProjectOptions(cid).then((res) => {
                                        if (cancelled) {
                                            return;
                                        }
                                        res.fold(
                                            () => {},
                                            (projs) => setProjectsMap((prev) => ({ ...prev, [cid]: projs }))
                                        );
                                    });
                                });
                            }
                        }
                    );
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    if (!cancelled) {
                        setLoading(false);
                    }
                });
        }

        return () => {
            cancelled = true;
        };
    }, [open, isEdit, userId, controller]);

    const addAssignment = () => {
        setAssignments([...assignments, { id: crypto.randomUUID(), clientId: '', projectId: '', profile: 'CF' }]);
    };

    const removeAssignment = (index: number) => {
        setAssignments(assignments.filter((_, i) => i !== index));
    };

    const updateAssignment = (index: number, field: keyof AssignmentRow, value: string) => {
        setAssignments((prev) =>
            prev.map((a, i) => {
                if (i !== index) {
                    return a;
                }
                const updated = { ...a, [field]: value };
                if (field === 'clientId') {
                    updated.projectId = '';
                }
                return updated;
            })
        );
        if (field === 'clientId' && value) {
            loadProjects(value);
        }
    };

    return {
        loading,
        name,
        email,
        phone,
        status,
        createdAt,
        lastLoginAt,
        assignments,
        clients,
        projectsMap,
        errors,
        setName,
        setEmail,
        setPhone,
        setErrors,
        addAssignment,
        removeAssignment,
        updateAssignment
    };
}
